"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.consumerTicketStatusPageSchema = exports.consumerTicketStatusSchema = exports.consumerTicketListQuerySchema = exports.consumerTicketQuerySchema = exports.CONSUMER_TICKETS_CURSOR_PATTERN = exports.CONSUMER_TICKET_STATUS_MAX_AGE_SECONDS = exports.CONSUMER_TICKETS_PAGE_SIZE_MAX = exports.CONSUMER_TICKETS_PAGE_SIZE_DEFAULT = void 0;
const zod_1 = require("zod");
const ticket_state_js_1 = require("./ticket-state.js");
const tickets_js_1 = require("./tickets.js");
/**
 * Lettura dello stato dei ticket dai consumer per «le mie segnalazioni» (DEC-013, story #73;
 * contratto di integrazione §3 superficie 4, §8). Due letture con il token del consumer: l'elenco
 * dei ticket di un segnalante e il singolo ticket per codice, entrambe nell'**ambito di lettura**
 * (prodotto e ambiente del token, committenti autorizzati, canale autenticato, segnalante indicato).
 * Chiavi in italiano e in camelCase come il resto del contratto verso i prodotti.
 *
 * Il contenuto è minimo per costruzione: codice, stato, categoria, istante di ricezione e
 * dell'ultimo cambio di stato. Mai priorità, assegnatario, collaboratori, note interne, testo della
 * segnalazione o thread (DEC-002 regola 6, DEC-013 regola 3).
 */
/** Ticket per pagina dell'elenco quando il consumer non indica `limite`. */
exports.CONSUMER_TICKETS_PAGE_SIZE_DEFAULT = 20;
/** Ticket per pagina al massimo: un `limite` più alto è rifiutato (400), non ridotto in silenzio. */
exports.CONSUMER_TICKETS_PAGE_SIZE_MAX = 50;
/**
 * Durata della cache lato consumer delle due letture, in secondi (`Cache-Control: private`):
 * breve, perché lo stato cambia mentre il supporto lavora il ticket, ma abbastanza da assorbire
 * aperture ripetute della lista dallo stesso utente (DEC-013 regola 5).
 */
exports.CONSUMER_TICKET_STATUS_MAX_AGE_SECONDS = 30;
/** Il cursore è opaco: il consumer lo ripete com'è, senza costruirlo né interpretarlo. */
exports.CONSUMER_TICKETS_CURSOR_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;
/**
 * Identificativo del segnalante, con le stesse regole di `segnalante.id` alla creazione: è quello
 * che il prodotto risolve dalla propria sessione, mai dal browser (easycare DEC-054).
 */
const segnalante = zod_1.z
    .string({ error: 'Segnalante: parametro obbligatorio' })
    .trim()
    .min(1, { error: 'Segnalante: parametro obbligatorio' })
    .max(tickets_js_1.REPORTER_ID_MAX, { error: `Segnalante: troppo lungo (massimo ${tickets_js_1.REPORTER_ID_MAX} caratteri)` });
/** Query di `GET /consumer/tickets/{codice}`: il segnalante delimita l'ambito anche sul singolo ticket. */
exports.consumerTicketQuerySchema = zod_1.z.object({ segnalante });
/** Query di `GET /consumer/tickets`: segnalante obbligatorio, pagina a cursore. */
exports.consumerTicketListQuerySchema = zod_1.z.object({
    segnalante,
    limite: zod_1.z.coerce
        .number({ error: 'Limite: deve essere un numero intero' })
        .int({ error: 'Limite: deve essere un numero intero' })
        .min(1, { error: 'Limite: almeno 1' })
        .max(exports.CONSUMER_TICKETS_PAGE_SIZE_MAX, { error: `Limite: al massimo ${exports.CONSUMER_TICKETS_PAGE_SIZE_MAX}` })
        .default(exports.CONSUMER_TICKETS_PAGE_SIZE_DEFAULT),
    cursore: zod_1.z
        .string({ error: 'Cursore: non valido' })
        .regex(exports.CONSUMER_TICKETS_CURSOR_PATTERN, { error: 'Cursore: non valido' })
        .optional(),
});
/** Stato di un ticket come lo vede il consumer: solo questi cinque campi. */
exports.consumerTicketStatusSchema = zod_1.z.object({
    codice: zod_1.z.string().meta({ description: 'Codice del ticket (DEC-003)', example: 'EC-2026-0001' }),
    stato: zod_1.z.enum(ticket_state_js_1.TICKET_STATES).meta({
        description: 'Uno dei sei stati di DEC-002; le etichette per chi ha segnalato sono del prodotto',
    }),
    categoria: zod_1.z.string().meta({ description: 'Categoria scelta alla segnalazione', example: 'Referti' }),
    ricevutoIl: zod_1.z.iso.datetime().meta({ description: 'Istante della ricezione, ISO 8601 in UTC' }),
    statoCambiatoIl: zod_1.z.iso.datetime().meta({
        description: 'Istante dell’ultimo cambio di stato, ISO 8601 in UTC; uguale a `ricevutoIl` finché il ticket è `nuovo`',
    }),
});
/** Una pagina dell'elenco, dal ticket ricevuto più di recente. */
exports.consumerTicketStatusPageSchema = zod_1.z.object({
    ticket: zod_1.z.array(exports.consumerTicketStatusSchema).meta({ description: 'Dal più recente per istante di ricezione' }),
    cursoreSuccessivo: zod_1.z.string().nullable().meta({
        description: 'Da ripetere in `cursore` per la pagina seguente; `null` sull’ultima pagina',
    }),
});
//# sourceMappingURL=consumer-ticket-status.js.map